import type {
  CategoryTreeNode,
  HomeAggregation,
  McpInfoDto,
  NavMenuDto,
  NowEntryDto,
  PageDto,
  Paginated,
  PostSummary,
  PublicPostResponse,
  SearchResponse,
  SearchTermStat,
  SiteConfigDto,
  TagDto,
  ToolKind,
  ToolResourceList,
  PublicToolResourceDto,
  WorkDto,
  WorkSummary,
  WpmPassage,
} from '@s-blog/shared-types';
import { http } from './http';

export const publicApi = {
  mcpInfo(): Promise<McpInfoDto> {
    return http.get<McpInfoDto>('/public/mcp/info').then((r) => r.data);
  },
  home(): Promise<HomeAggregation> {
    return http.get<HomeAggregation>('/public/home').then((r) => r.data);
  },
  listPosts(params: {
    type?: 'BLOG' | 'NOTE';
    categoryId?: string;
    tagId?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<Paginated<PostSummary>> {
    return http.get<Paginated<PostSummary>>('/public/posts', { params }).then((r) => r.data);
  },
  getPost(slug: string): Promise<PublicPostResponse> {
    return http.get<PublicPostResponse>(`/public/posts/${encodeURIComponent(slug)}`).then((r) => r.data);
  },
  // 副作用接口：5 分钟 IP+slug 节流由后端处理；前端进入详情页直接调即可
  incrementView(slug: string): Promise<{ ok: true; count?: number }> {
    return http
      .post<{ ok: true; count?: number }>(`/public/posts/${encodeURIComponent(slug)}/view`)
      .then((r) => r.data);
  },
  // 「复制为 Markdown」计数. 复制成功后 fire-and-forget 调用; 后端 IP+slug 节流.
  incrementCopy(slug: string): Promise<{ ok: true; count?: number }> {
    return http
      .post<{ ok: true; count?: number }>(`/public/posts/${encodeURIComponent(slug)}/copy`)
      .then((r) => r.data);
  },
  // 匿名点赞. 后端 IP+slug 10 分钟节流, 客户端 localStorage 防 UI 重复.
  incrementLike(slug: string): Promise<{ ok: true; count?: number }> {
    return http
      .post<{ ok: true; count?: number }>(`/public/posts/${encodeURIComponent(slug)}/like`)
      .then((r) => r.data);
  },
  listWorks(): Promise<WorkSummary[]> {
    return http.get<WorkSummary[]>('/public/works').then((r) => r.data);
  },
  // ⚠ 后端已知 bug：不存在或非 PUBLISHED 时返回 500（应 404），
  // 前台收到任意失败都按"作品不存在"处理
  getWork(slug: string): Promise<WorkDto> {
    return http.get<WorkDto>(`/public/works/${encodeURIComponent(slug)}`).then((r) => r.data);
  },
  // 站长自定义页面。未发布/私密 -> 404；前台收到失败按"页面不存在"处理
  getPage(slug: string): Promise<PageDto> {
    return http.get<PageDto>(`/public/pages/${encodeURIComponent(slug)}`).then((r) => r.data);
  },
  listNow(limit = 20): Promise<NowEntryDto[]> {
    return http.get<NowEntryDto[]>('/public/now', { params: { limit } }).then((r) => r.data);
  },
  // type 可选: 传 'NOTE'/'BLOG' 时 postCount 只数该类文章 (notes 页据此过滤空分类)
  categoryTree(type?: 'BLOG' | 'NOTE'): Promise<CategoryTreeNode[]> {
    return http
      .get<CategoryTreeNode[]>('/public/categories', { params: type ? { type } : {} })
      .then((r) => r.data);
  },
  listTags(): Promise<TagDto[]> {
    return http.get<TagDto[]>('/public/tags').then((r) => r.data);
  },
  navMenus(): Promise<NavMenuDto[]> {
    return http.get<NavMenuDto[]>('/public/nav-menus').then((r) => r.data);
  },
  wpmPassage(): Promise<WpmPassage> {
    return http.get<WpmPassage>('/public/wpm/passage').then((r) => r.data);
  },
  listTools(kind: ToolKind, params: { page?: number; pageSize?: number; tag?: string } = {}): Promise<ToolResourceList> {
    const segment = kind === 'SKILL' ? 'skills' : 'mcps';
    return http.get<ToolResourceList>(`/public/${segment}`, { params }).then((r) => r.data);
  },
  getTool(kind: ToolKind, slug: string): Promise<PublicToolResourceDto> {
    const segment = kind === 'SKILL' ? 'skills' : 'mcps';
    return http.get<PublicToolResourceDto>(`/public/${segment}/${encodeURIComponent(slug)}`).then((r) => r.data);
  },
};

export const siteConfigApi = {
  // 公开接口；后端已脱敏 qiniuSecretKey（只返 qiniuSecretKeyConfigured: boolean）
  get(): Promise<SiteConfigDto> {
    return http.get<SiteConfigDto>('/site-config').then((r) => r.data);
  },
};

export interface AskSource {
  type: 'POST' | 'WORK';
  slug: string;
  title: string;
  url: string;
}

// 多轮对话历史 (随请求带上)
export interface AskTurn {
  role: 'user' | 'assistant';
  content: string;
}

export const searchApi = {
  // mode: keyword | semantic | hybrid (默认 hybrid; 后端无 DashScope key 时自动退 keyword)
  search(q: string, limit = 20, mode: 'keyword' | 'semantic' | 'hybrid' = 'hybrid'): Promise<SearchResponse> {
    return http.get<SearchResponse>('/search', { params: { q, limit, mode } }).then((r) => r.data);
  },

  // 热门搜索词 (kind='search') / 热门提问 (kind='ask'). 给搜索框热门提示 + RAG 推荐问题.
  topTerms(kind: 'search' | 'ask', limit = 8): Promise<SearchTermStat[]> {
    return http.get<SearchTermStat[]>('/search/top-terms', { params: { kind, limit } }).then((r) => r.data);
  },

  /**
   * RAG 问答 — SSE 流式. onToken 逐段答案, onSources 来源数组.
   * history: 多轮对话历史 (不含本轮 question). 用 fetch + ReadableStream (EventSource 不支持 POST). 返回 abort 函数.
   */
  ask(
    question: string,
    history: AskTurn[],
    handlers: { onToken: (t: string) => void; onSources: (s: AskSource[]) => void; onError: (m: string) => void; onDone: () => void },
  ): () => void {
    const ctrl = new AbortController();
    const base = (import.meta.env.VITE_API_BASE_URL as string) ?? '/api';
    (async () => {
      try {
        const res = await fetch(`${base}/search/ask`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question, history }),
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) {
          handlers.onError(res.status === 503 ? 'AI 问答未启用' : `请求失败 (${res.status})`);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const frames = buf.split('\n\n');
          buf = frames.pop() ?? '';
          for (const frame of frames) {
            const ev = /event: (.+)/.exec(frame)?.[1]?.trim();
            const dataLine = /data: (.+)/s.exec(frame)?.[1];
            if (!ev || dataLine == null) continue;
            let data: unknown;
            try { data = JSON.parse(dataLine); } catch { continue; }
            if (ev === 'token') handlers.onToken(data as string);
            else if (ev === 'sources') handlers.onSources(data as AskSource[]);
            else if (ev === 'error') handlers.onError((data as { message: string }).message);
            else if (ev === 'done') handlers.onDone();
          }
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') handlers.onError(String(e).slice(0, 120));
      }
    })();
    return () => ctrl.abort();
  },
};
